import isNumber from 'lodash/isNumber.js';

function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }
    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}
const nextFrame = _async(function () {
  return new Promise(resolve => {
    requestAnimationFrame(resolve);
  });
});
function getStyle(el) {
  return window.getComputedStyle ? getComputedStyle(el, null) : el.currentStyle;
}
function getZIndex(element) {
  const {
    zIndex
  } = getStyle(element);
  return parseInt(zIndex);
}
function getWidth(element) {
  const {
    width
  } = getStyle(element);
  return parseFloat((width || '').replace('px', ''));
}
function getHeight(element) {
  const {
    height
  } = getStyle(element);
  return parseFloat((height || '').replace('px', ''));
}
function getBorderWidth(element) {
  const {
    borderWidth
  } = getStyle(element);
  return parseFloat((borderWidth || '').replace('px', ''));
}
function setStyles(element, styles) {
  if (element) {
    Object.keys(styles).forEach(prop => {
      let unit = '';
      const numericUnits = ['minWidth', 'maxWidth', 'minHeight', 'maxHeight', 'width', 'height', 'top', 'right', 'bottom', 'left', 'marginLeft', 'marginRight', 'marginTop', 'marginBottom'];
      if (numericUnits.includes(prop) && isNumber(styles[prop])) {
        unit = 'px';
      }
      element.style[prop] = styles[prop] + unit;
    });
  }
}
function getMargin(el) {
  const style = getStyle(el);
  return {
    marginLeft: parseInt(style.marginLeft) || 0,
    marginRight: parseInt(style.marginRight) || 0,
    marginTop: parseInt(style.marginTop) || 0,
    marginBottom: parseInt(style.marginBottom) || 0
  };
}
function setAttributes(element, attributes) {
  Object.keys(attributes).forEach(prop => {
    const value = attributes[prop];
    if (value !== false) {
      element.setAttribute(prop, attributes[prop]);
    } else {
      element.removeAttribute(prop);
    }
  });
}
function hasClass(element, cls) {
  return element && element.classList.contains(cls);
}
function removeClass(element, cls) {
  if (element && hasClass(element, cls)) {
    element.classList.remove(cls);
  }
}
function addClass(element, cls) {
  if (element && !hasClass(element, cls)) {
    element.classList.add(cls);
  }
}
function getTarget() {
  let node = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : document.body;
  if (node === true) {
    return document.body;
  }
  return node instanceof window.Node ? node : document.querySelector(node);
}
function elementPositionInAncestor(el, ancestor) {
  let top = 0;
  let left = 0;
  do {
    top += el.offsetTop || 0;
    left += el.offsetLeft || 0;
    el = el.offsetParent;
  } while (el && el !== ancestor);
  return {
    top,
    left
  };
}
function getScrollParent(node) {
  const isElement = node instanceof HTMLElement;
  const overflowY = isElement && getStyle(node).overflowY;
  const isScrollable = overflowY !== 'visible' && overflowY !== 'hidden';
  if (!node) {
    return null;
  }
  if (isScrollable && node.scrollHeight >= node.clientHeight) {
    return node;
  }
  return getScrollParent(node.parentNode) || document.body;
}
function raf(fn) {
  let lastArgs;
  let activeFrame = null;
  const wrapperFn = function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    lastArgs = args;
    if (activeFrame) {
      return;
    }
    activeFrame = requestAnimationFrame(() => {
      activeFrame = null;
      fn(...lastArgs);
    });
  };
  wrapperFn.cancel = () => {
    if (!activeFrame) {
      return;
    }
    cancelAnimationFrame(activeFrame);
    activeFrame = null;
  };
  return wrapperFn;
}
function elementHasOverflowY(_ref) {
  let {
    clientHeight,
    scrollHeight
  } = _ref;
  return scrollHeight > clientHeight;
}
function elementHasOverflowX(_ref2) {
  let {
    clientWidth,
    scrollWidth
  } = _ref2;
  return scrollWidth > clientWidth;
}
function elementHasOverflow(element) {
  return elementHasOverflowY(element) || elementHasOverflowX(element);
}
function elementHasScrollX(element) {
  const hasOverflow = elementHasOverflowX(element);
  const {
    overflowX
  } = getStyle(element);
  return hasOverflow && ['auto', 'scroll'].includes(overflowX);
}
function elementHasScrollY(element) {
  const hasOverflow = elementHasOverflowY(element);
  const {
    overflowY
  } = getStyle(element);
  return hasOverflow && ['auto', 'scroll'].includes(overflowY);
}
function findNodeByContent(text) {
  let root = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : document.body;
  const treeWalker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodeList = [];
  while (treeWalker.nextNode()) {
    const node = treeWalker.currentNode;
    if (node.nodeType === Node.TEXT_NODE && node.textContent.includes(text)) {
      nodeList.push(node.parentNode);
    }
  }
  return nodeList;
}
function isPx(val) {
  return val && /^\d+(px)$/.test(val);
}
function isPercent(val) {
  return val && /^\d+%$/.test(val);
}
function isVNode(node) {
  return node !== null && typeof node === 'object' && Object.hasOwn(node, 'componentOptions');
}
function getAllVisibleOverlays() {
  const componentOverlaySelectors = ['.pendo-modal.is-open', '.pendo-drawer.is-open', '.pendo-notification'];
  return componentOverlaySelectors.reduce((acc, selector) => {
    const elements = document.querySelectorAll(selector);
    return acc.concat(...elements);
  }, []).filter(el => el.style.display !== 'none');
}
function getTopZindex() {
  return getAllVisibleOverlays().reduce((acc, el) => {
    const zIndex = getZIndex(el);
    if (zIndex > acc) {
      return zIndex;
    }
    return acc;
  }, 0);
}
let scrollBarWidth;
let scrollBarHeight;
function getScrollbarWidth() {
  if (scrollBarWidth !== undefined) {
    return scrollBarWidth;
  }
  const outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.width = '100px';
  outer.style.msOverflowStyle = 'scrollbar';
  document.body.appendChild(outer);
  const widthNoScroll = outer.offsetWidth;
  outer.style.overflow = 'scroll';
  const inner = document.createElement('div');
  inner.style.width = '100%';
  outer.appendChild(inner);
  const widthWithScroll = inner.offsetWidth;
  outer.parentNode.removeChild(outer);
  scrollBarWidth = widthNoScroll - widthWithScroll;
  return scrollBarWidth;
}
function getScrollbarHeight() {
  if (scrollBarHeight !== undefined) {
    return scrollBarHeight;
  }
  const outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.height = '100px';
  outer.style.msOverflowStyle = 'scrollbar';
  document.body.appendChild(outer);
  const heightNoScroll = outer.offsetHeight;
  outer.style.overflow = 'scroll';
  const inner = document.createElement('div');
  inner.style.height = '100%';
  outer.appendChild(inner);
  const heightWithScroll = inner.offsetHeight;
  outer.parentNode.removeChild(outer);
  scrollBarHeight = heightNoScroll - heightWithScroll;
  return scrollBarHeight;
}

export { addClass, elementHasOverflow, elementHasOverflowX, elementHasOverflowY, elementHasScrollX, elementHasScrollY, elementPositionInAncestor, findNodeByContent, getAllVisibleOverlays, getBorderWidth, getHeight, getMargin, getScrollParent, getScrollbarHeight, getScrollbarWidth, getStyle, getTarget, getTopZindex, getWidth, getZIndex, hasClass, isPercent, isPx, isVNode, nextFrame, raf, removeClass, setAttributes, setStyles };
//# sourceMappingURL=dom.js.map
